<template>
  <v-card
    v-if="variant === 'default'"
    outlined
    rounded
    class="payment-method-details payment-method-details--default px-4 py-4 min-width-0"
    :class="{ 'payment-method-details--highlight': highlight }"
    :data-test="'payment-method-details-default-' + method.id"
  >
    <div class="payment-method-details__accent" />

    <PaymentMethodDetailsBody
      variant="default"
      :brand-icon-name="brandIconName"
      :type-label="typeLabel"
      :detail="detail"
      :detail-separator="detailSeparator"
      :secondary-line="secondaryLine"
      :holder="method.holder"
    >
      <template #trailing>
        <div class="d-flex flex-column align-end flex-shrink-0">
          <v-chip
            v-if="method.is_default"
            pill
            small
            class="chip--badge payment-method-details__chip payment-method-details__chip--default font-weight-semi-bold"
          >
            {{ $t('fields.account.billing.payment.payment_methods_default_badge') }}
          </v-chip>
          <v-chip
            v-if="isExpired"
            pill
            small
            class="chip--badge payment-method-details__chip payment-method-details__chip--expired font-weight-semi-bold"
            :class="{ 'mt-2': method.is_default }"
          >
            {{ $t('fields.account.billing.payment.payment_method_expired') }}
          </v-chip>
        </div>
      </template>
    </PaymentMethodDetailsBody>
  </v-card>

  <PaymentMethodDetailsBody
    v-else
    variant="row"
    :class="{ 'payment-method-details--highlight': highlight }"
    :data-test="`payment-method-details-row-${method.id}`"
    :brand-icon-name="brandIconName"
    :type-label="typeLabel"
    :detail="detail"
    :detail-separator="detailSeparator"
    :secondary-line="secondaryLine"
  >
    <template #trailing>
      <div class="d-flex align-center flex-shrink-0 ml-2">
        <v-chip
          v-if="isExpired"
          pill
          small
          class="chip--badge payment-method-details__chip payment-method-details__chip--expired font-weight-semi-bold"
        >
          {{ $t('fields.account.billing.payment.payment_method_expired') }}
        </v-chip>

        <v-progress-circular
          v-if="isSettingDefault"
          indeterminate
          :width="3"
          :size="24"
          color="candy"
          class="payment-method-details__row-spinner ml-1"
          :data-test="`payment-method-row-setting-default-${method.id}`"
        />

        <v-menu v-else-if="showRowMenu" offset-y left content-class="menu--select payment-method-details__menu">
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              small
              :ripple="false"
              class="payment-method-details__menu-btn ml-1"
              :data-test="`payment-method-row-menu-${method.id}`"
              :aria-label="menuAriaLabel"
              v-bind="attrs"
              v-on="on"
            >
              <Icon name="more-options-system" outline color="lapis" />
            </v-btn>
          </template>

          <v-list dense>
            <v-list-item
              v-if="showSetAsDefault"
              :data-test="`payment-method-row-set-default-${method.id}`"
              @click="onSetAsDefault"
            >
              <v-list-item-title>
                {{ $t('fields.account.billing.payment.payment_methods_set_default') }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="showRemove"
              class="payment-method-details__menu-item--remove"
              :data-test="`payment-method-row-remove-${method.id}`"
              @click="onRemove"
            >
              <v-list-item-title>
                {{ $t('fields.account.billing.payment.payment_methods_remove') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </template>
  </PaymentMethodDetailsBody>
</template>

<script lang="ts">
import type { SavedCardPaymentMethod, SavedPaymentMethod } from '@/classes/stripe';
import { isManageablePaymentMethod } from '@/classes/stripe';
import Icon from '@/components/Icons/Icon.vue';
import PaymentMethodDetailsBody from '@/components/Subscription/PaymentMethodDetailsBody.vue';
import {
  getSavedPaymentMethodEmail,
  getSavedPaymentMethodTypeLabel,
} from '@/Services/PaymentMethod/paymentMethodLabels';
import dayjs from 'dayjs';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  components: {
    Icon,
    PaymentMethodDetailsBody,
  },
})
export default class PaymentMethodDetails extends Vue {
  @Prop({ required: true })
  method!: SavedPaymentMethod;

  @Prop({ default: 'default' })
  variant!: 'default' | 'row';

  @Prop({ default: false })
  showActions!: boolean;

  @Prop({ default: false })
  isSettingDefault!: boolean;

  @Prop({ default: false })
  actionsDisabled!: boolean;

  @Prop({ default: false })
  highlight!: boolean;

  get isManageable(): boolean {
    return isManageablePaymentMethod(this.method);
  }

  get showSetAsDefault(): boolean {
    return this.isManageable && !this.method.is_default && !this.isExpired;
  }

  get showRemove(): boolean {
    return this.isManageable && !this.method.is_default;
  }

  get showRowMenu(): boolean {
    return this.showActions && this.isManageable && !this.actionsDisabled && (this.showSetAsDefault || this.showRemove);
  }

  get menuAriaLabel(): string {
    return this.$t('fields.account.billing.payment.payment_methods_row_menu').toString();
  }

  /** The method narrowed to a card, or null for non-card types. */
  get cardMethod(): SavedCardPaymentMethod | null {
    return this.method.type === 'card' ? this.method : null;
  }

  get brandIconName(): string | false {
    if (this.method.type === 'card') {
      const icons: Record<string, string> = {
        mastercard: 'pay-mastercard-system',
        visa: 'pay-visa-system',
        amex: 'pay-amex-system',
      };
      return icons[this.method.brand?.toLowerCase()] || false;
    }
    const icons: Record<string, string> = {
      paypal: 'pay-paypal-system',
      klarna: 'pay-klarna-system',
      sepa_debit: 'pay-sepa-system',
      pix: 'pay-pix-system',
      bank_transfer: 'pay-invoice-system',
    };
    return icons[this.method.type] || false;
  }

  /** The bold type name shown for the method. */
  get typeLabel(): string {
    return getSavedPaymentMethodTypeLabel(this.method, (key) => this.$t(key).toString());
  }

  /** The lighter detail (masked number / email) shown next to the type name. */
  get detail(): string {
    switch (this.method.type) {
      case 'paypal':
      case 'klarna':
        return getSavedPaymentMethodEmail(this.method);
      case 'sepa_debit':
        return this.method.last4 ? `•••• ${this.method.last4}` : '';
      case 'bank_transfer':
      case 'pix':
        return '';
      case 'card':
      default:
        return this.cardMethod?.last4 ? `•••• ${this.cardMethod.last4}` : '';
    }
  }

  /**
   * Separator between the type name and its detail. Email-bearing types
   * (PayPal / Klarna) read as "PayPal - email@example.com"; masked-number
   * types are simply spaced ("Visa •••• 4242").
   */
  get detailSeparator(): string {
    return this.method.type === 'paypal' || this.method.type === 'klarna' ? ' - ' : ' ';
  }

  get formattedExpiry(): string {
    const card = this.cardMethod;
    if (!card) {
      return '';
    }
    const month = String(card.exp_month).padStart(2, '0');
    const year = String(Number(card.exp_year) % 100).padStart(2, '0');
    return `${month}/${year}`;
  }

  get formattedAddedDate(): string | null {
    if (!this.method.created_at) {
      return null;
    }
    const parsed = dayjs(this.method.created_at);
    if (!parsed.isValid()) {
      return null;
    }
    return parsed.format('MMM YYYY');
  }

  get secondaryLine(): string {
    // Non-card types have no expiry; show only the "Saved" date when available.
    if (!this.cardMethod) {
      return this.formattedAddedDate
        ? this.$t('fields.account.billing.payment.payment_methods_saved', { date: this.formattedAddedDate }).toString()
        : '';
    }
    if (this.formattedAddedDate) {
      return this.$t('fields.account.billing.payment.payment_methods_expires_added', {
        expiry: this.formattedExpiry,
        date: this.formattedAddedDate,
      }).toString();
    }
    return `${this.$t('fields.account.billing.payment.payment_method_expires')} ${this.formattedExpiry}`;
  }

  get isExpired(): boolean {
    const card = this.cardMethod;
    if (!card) {
      return false;
    }
    const year = Number(card.exp_year);
    const month = Number(card.exp_month);
    const now = dayjs();
    return year < now.year() || (year === now.year() && month < now.month() + 1);
  }

  onSetAsDefault(): void {
    this.$emit('set-default', this.method);
  }

  onRemove(): void {
    this.$emit('remove', this.method);
  }
}
</script>

<style lang="scss" scoped>
.payment-method-details--default {
  position: relative;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  border-color: rgba(map-get($navy, 'base'), 0.32) !important;
}

.payment-method-details__accent {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: rem(4);
  background-color: map-get($navy, 'lighten-1') !important;
}

.payment-method-details__chip {
  &--default {
    background-color: rgba(map-get($navy, 'base'), 0.14) !important;
    color: map-get($navy, 'darken-1') !important;
  }

  &--expired {
    background-color: rgba(map-get($begonia, 'base'), 0.12) !important;
    color: map-get($begonia, 'base') !important;
  }
}

.payment-method-details__menu-btn {
  width: rem(32) !important;
  height: rem(32) !important;
}

.payment-method-details__row-spinner {
  flex-shrink: 0;
}

// Newly added payment methods briefly highlight (warm background + amber
// border), hold for ~3s, then ease back to their resting style.
.payment-method-details--default.payment-method-details--highlight {
  animation: payment-method-highlight-default 3.5s ease;
}

.payment-method-details--row.payment-method-details--highlight {
  animation: payment-method-highlight-row 3.5s ease;
}

@keyframes payment-method-highlight-default {
  0%,
  85% {
    background-color: #fffaeb;
    box-shadow: inset 0 0 0 1px #ffdc7c;
  }
  100% {
    background-color: #fff;
    box-shadow: inset 0 0 0 1px transparent;
  }
}

@keyframes payment-method-highlight-row {
  0%,
  85% {
    background-color: #fffaeb;
    box-shadow: inset 0 0 0 1px #ffdc7c;
  }
  100% {
    background-color: transparent;
    box-shadow: inset 0 0 0 1px transparent;
  }
}
</style>

<!-- v-menu content is teleported to body; scoped styles cannot reach it -->
<style lang="scss">
.payment-method-details__menu.v-menu__content {
  margin-top: rem(4);

  .v-list {
    padding: rem(4);
  }

  .v-list-item {
    min-height: 0;
    min-width: 150px;
    padding: rem(8) rem(12);

    &__title {
      font-size: rem(14);
    }
  }

  .payment-method-details__menu-item--remove {
    .v-list-item__title {
      color: #c93545;
    }

    &:hover {
      background-color: #ffeef0;
      color: #c93545;
    }
  }
}
</style>
